/**
 * Command Palette registry (P2.1-S4).
 *
 * Static registry các "Quick action" + "Page" — items không phụ thuộc query.
 * Search results (forms/workflows/rules/translations) build dynamic ở component.
 *
 * Doc 32 §4.
 */
import {
  Activity,
  Archive,
  Bell,
  Boxes,
  Coins,
  Cpu,
  FileSearch,
  FileText,
  Globe,
  Languages,
  LayoutDashboard,
  type LucideIcon,
  MessageSquarePlus,
  Plus,
  Settings,
  Sparkles,
  Users,
  Workflow,
} from "lucide-react";

import type { RouteName } from "@/stores/route";

export interface PaletteItem {
  id: string;
  group: "action" | "page";
  label: string;
  hint?: string;
  icon: LucideIcon;
  /** Required role để show. Empty = mọi user. */
  requiredRole?: "admin" | "auditor";
  /** Action: navigate to route + optional param. */
  route: RouteName;
  param?: string;
  /** Optional keywords for matching (lowercase). */
  keywords?: string[];
}

export const QUICK_ACTIONS: PaletteItem[] = [
  {
    id: "action.new-form",
    group: "action",
    label: "Tạo form mới",
    hint: "Mở Forms list để bấm Tạo",
    icon: Plus,
    requiredRole: "admin",
    route: "admin.forms",
    keywords: ["new", "tạo", "form", "create"],
  },
  {
    id: "action.new-workflow",
    group: "action",
    label: "Tạo workflow",
    hint: "Mở Workflows list",
    icon: Plus,
    requiredRole: "admin",
    route: "admin.workflows",
    keywords: ["new", "tạo", "workflow", "flow", "create"],
  },
  {
    id: "action.new-chat",
    group: "action",
    label: "Mở chat mới",
    icon: MessageSquarePlus,
    route: "chat",
    keywords: ["chat", "conversation", "hội thoại", "new", "mới"],
  },
];

export const PAGE_ITEMS: PaletteItem[] = [
  {
    id: "page.chat",
    group: "page",
    label: "Chat",
    icon: MessageSquarePlus,
    route: "chat",
    keywords: ["chat", "hội thoại"],
  },
  {
    id: "page.forms",
    group: "page",
    label: "Forms",
    hint: "Quản lý form definitions",
    icon: FileText,
    requiredRole: "admin",
    route: "admin.forms",
    keywords: ["form", "field"],
  },
  {
    id: "page.workflows",
    group: "page",
    label: "Workflows",
    hint: "Quản lý workflow",
    icon: Workflow,
    requiredRole: "admin",
    route: "admin.workflows",
    keywords: ["workflow", "flow", "automation"],
  },
  {
    id: "page.notifications",
    group: "page",
    label: "Notifications",
    hint: "Channels / Templates / Rules",
    icon: Bell,
    requiredRole: "admin",
    route: "admin.notifications",
    keywords: ["notification", "thông báo", "rule"],
  },
  {
    id: "page.translations",
    group: "page",
    label: "Translations",
    hint: "i18n keys",
    icon: Languages,
    requiredRole: "admin",
    route: "admin.translations",
    keywords: ["translation", "i18n", "ngôn ngữ", "dịch"],
  },
  {
    id: "page.audit",
    group: "page",
    label: "Audit Logs",
    icon: FileSearch,
    requiredRole: "admin",
    route: "admin.audit",
    keywords: ["audit", "log", "history"],
  },
  {
    id: "page.odoo-modules",
    group: "page",
    label: "Odoo Modules",
    hint: "Catalog + AI auto-form generator",
    icon: Boxes,
    requiredRole: "admin",
    route: "admin.odooModules",
    keywords: ["odoo", "module", "auto", "form", "gen"],
  },
  {
    id: "page.external-systems",
    group: "page",
    label: "External Systems",
    hint: "Git / DB / URL → AI doc",
    icon: Globe,
    requiredRole: "admin",
    route: "admin.externalSystems",
    keywords: ["external", "git", "database", "url", "doc", "kb"],
  },
  {
    id: "page.dashboards",
    group: "page",
    label: "Dashboards",
    hint: "Generative AI dashboards",
    icon: LayoutDashboard,
    requiredRole: "admin",
    route: "admin.dashboards",
    keywords: ["dashboard", "kpi", "chart", "metric", "generative"],
  },
  {
    id: "page.cost",
    group: "page",
    label: "Cost Analytics",
    hint: "LLM cost dashboard",
    icon: Coins,
    requiredRole: "admin",
    route: "admin.cost",
    keywords: ["cost", "billing", "tokens", "usage", "analytics"],
  },
  {
    id: "page.health",
    group: "page",
    label: "System Health",
    hint: "Realtime component status",
    icon: Activity,
    requiredRole: "admin",
    route: "admin.health",
    keywords: ["health", "status", "monitor", "uptime"],
  },
  {
    id: "page.localLlm",
    group: "page",
    label: "Local LLM (Ollama)",
    hint: "On-prem inference + tenant on-prem toggle",
    icon: Cpu,
    requiredRole: "admin",
    route: "admin.localLlm",
    keywords: ["ollama", "local", "on-prem", "qwen", "llama", "self-host"],
  },
  {
    id: "page.adminInsights",
    group: "page",
    label: "AI Insights (Admin)",
    hint: "Weekly summary all users + manual trigger",
    icon: Sparkles,
    requiredRole: "admin",
    route: "admin.insights",
    keywords: ["insights", "summary", "recap", "weekly", "tổng kết"],
  },
  {
    id: "page.users",
    group: "page",
    label: "Users (Admin)",
    hint: "Quản lý user + role + Odoo identity mapping",
    icon: Users,
    requiredRole: "admin",
    route: "admin.users",
    keywords: ["users", "user", "admin", "role", "odoo", "mapping", "password"],
  },
  {
    id: "page.myInsights",
    group: "page",
    label: "Insights (Tổng kết tuần)",
    hint: "AI tổng kết hoạt động hàng tuần của bạn",
    icon: Sparkles,
    route: "me.insights",
    keywords: ["insights", "summary", "recap", "weekly", "my", "tôi"],
  },
  {
    id: "page.backup",
    group: "page",
    label: "Backup & Restore",
    hint: "Export/import form+workflow",
    icon: Archive,
    requiredRole: "admin",
    route: "admin.backup",
    keywords: ["backup", "export", "import", "restore", "migration"],
  },
  {
    id: "page.dashboard",
    group: "page",
    label: "Admin Dashboard",
    icon: LayoutDashboard,
    requiredRole: "admin",
    route: "admin",
    keywords: ["dashboard", "stats", "thống kê", "admin"],
  },
  {
    id: "page.settings",
    group: "page",
    label: "Cài đặt cá nhân",
    icon: Settings,
    route: "settings.personal",
    keywords: ["settings", "cài đặt", "profile", "personal"],
  },
];

/**
 * Filter items theo role + lowercased query.
 *
 * Match khi query rỗng → return all (dùng cho HÀNH ĐỘNG / TRANG sections).
 * Match khi có query → check label/keyword chứa query (substring).
 */
export function filterPaletteItems(
  items: PaletteItem[],
  roles: string[],
  query: string,
): PaletteItem[] {
  const q = query.trim().toLowerCase();
  return items.filter((it) => {
    // Role check
    if (it.requiredRole === "admin" && !roles.includes("admin")) return false;
    if (it.requiredRole === "auditor") {
      if (!roles.includes("auditor") && !roles.includes("admin")) return false;
    }
    if (q.length === 0) return true;
    if (it.label.toLowerCase().includes(q)) return true;
    if (it.hint?.toLowerCase().includes(q)) return true;
    if (it.keywords?.some((k) => k.includes(q))) return true;
    return false;
  });
}
